import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Flame, MessageCircle, Phone } from "lucide-react";
import { http, euro } from "@/lib/api";
import { CATEGORIES, SITE } from "@/config/site";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Motion";

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const MenuRow = ({ p }) => (
  <motion.li
    data-testid={`menu-item-${p.id}`}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.6 }}
    className="group grid grid-cols-[auto_1fr_auto] items-center gap-4 sm:gap-6 py-5 border-b border-white/10"
  >
    <div className="relative w-16 h-16 sm:w-20 sm:h-20 overflow-hidden rounded-2xl bg-[#16161A] border border-white/10">
      {p.image ? (
        <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      ) : (
        <Flame className="absolute inset-0 m-auto w-7 h-7 text-[#FF5500]" />
      )}
    </div>
    <div className="min-w-0">
      <h3 className="font-display uppercase text-base sm:text-2xl leading-tight tracking-[-0.01em] transition-colors duration-300 group-hover:text-[#FF5500]">{p.name}</h3>
      {p.description && <p className="mt-1 text-sm text-zinc-400 leading-snug">{p.description}</p>}
    </div>
    <span data-testid={`menu-item-price-${p.id}`} className="font-display text-lg sm:text-2xl text-[#FF5500] tabular-nums whitespace-nowrap">{euro(p.price)}</span>
  </motion.li>
);

const Pills = ({ cats, active, onPick }) => (
  <div className="sticky top-[76px] sm:top-[92px] z-30 -mx-5 sm:mx-0 px-5 sm:px-0 py-3 bg-[#0D0D0F]/80 backdrop-blur-xl">
    <div className="flex gap-2 overflow-x-auto no-scrollbar">
      {["Tutti", ...cats].map((c) => (
        <button key={c} type="button" data-testid={`menu-filter-${slug(c)}`} onClick={() => onPick(c)} className={`relative shrink-0 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${active === c ? "border-[#FF5500] text-[#0D0D0F]" : "border-white/15 text-zinc-300 hover:border-white/40"}`}>
          {active === c && <motion.span layoutId="menu-pill" className="absolute inset-0 rounded-full bg-[#FF5500]" transition={{ type: "spring", stiffness: 420, damping: 34 }} />}
          <span className="relative z-10">{c}</span>
        </button>
      ))}
    </div>
  </div>
);

export default function MenuPage() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [active, setActive] = useState("Tutti");

  useEffect(() => {
    let alive = true;
    http
      .get("/products")
      .then((r) => {
        if (!alive) return;
        setItems(Array.isArray(r.data) ? r.data : []);
        setStatus("ok");
      })
      .catch(() => {
        if (alive) setStatus("error");
      });
    return () => {
      alive = false;
    };
  }, []);

  const cats = useMemo(() => CATEGORIES.filter((c) => items.some((p) => p?.category === c)), [items]);
  const shown = active === "Tutti" ? cats : cats.filter((c) => c === active);

  return (
    <div data-testid="menu-page">
      <PageHeader testid="menu-header" eyebrow="Il menù di Mister Pollo" lines={[{ text: "Il" }, { text: "Menù.", className: "text-[#FF5500]" }]} text="Tutto ciò che puoi degustare dal nostro girarrosto." />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        {status === "loading" && <p data-testid="menu-loading" className="py-20 text-zinc-400">Accendiamo lo spiedo…</p>}
        {status === "error" && <p data-testid="menu-error" className="py-20 text-zinc-300">Il menù non è disponibile al momento. Chiamaci per sapere cosa gira oggi!</p>}
        {status === "ok" && cats.length === 0 && <p data-testid="menu-empty" className="py-20 text-zinc-300">Il menù è in aggiornamento.</p>}
        {cats.length > 0 && <Pills cats={cats} active={active} onPick={setActive} />}
        {shown.map((c, i) => (
          <div key={c} data-testid={`menu-category-${slug(c)}`} className="grid gap-6 lg:grid-cols-12 pt-16">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-44">
                <span className="font-display text-6xl sm:text-7xl text-outline">{String(CATEGORIES.indexOf(c) + 1).padStart(2, "0")}</span>
                <h2 className="mt-3 font-display uppercase text-3xl sm:text-4xl tracking-[-0.03em] leading-none">{c}</h2>
                <p className="mt-2 text-sm text-zinc-400">{items.filter((p) => p.category === c).length} proposte</p>
              </div>
            </div>
            <ul className="lg:col-span-8 border-t border-white/10">
              {items.filter((p) => p.category === c).map((p) => <MenuRow key={p.id} p={p} />)}
            </ul>
          </div>
        ))}
        <Reveal className="mt-24">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 rounded-[2rem] bg-[#FF5500] text-[#0D0D0F] p-8 sm:p-10">
            <p className="font-display uppercase text-3xl sm:text-4xl leading-none tracking-[-0.02em]">Hai scelto?<br />Ordina ora.</p>
            <div className="flex flex-wrap gap-3">
              <a href={SITE.phoneHref} data-testid="menu-cta-call" className="inline-flex items-center gap-2 rounded-full bg-[#0D0D0F] text-white px-6 py-3.5 font-bold transition-transform hover:-translate-y-0.5"><Phone className="w-4 h-4" /> Chiama</a>
              <a href={SITE.whatsappHref} target="_blank" rel="noreferrer" data-testid="menu-cta-whatsapp" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0D0D0F] px-6 py-3 font-bold transition-transform hover:-translate-y-0.5"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
